import React, { useEffect, useMemo, useState } from 'react';
import { useSnapshot } from 'valtio';
import { AiFillCheckCircle } from 'react-icons/ai';
import { MdDelete, MdEmail, MdNotifications, MdPaid, MdRefresh, MdSearch, MdSend, MdClose, MdPeople, MdCheckCircle, MdWarning, MdPhoneIphone } from 'react-icons/md';
import { PushNotificationTestRequest, User } from '../@backend';
import useApi from '../hooks/useApi';
import { userState } from '../store';
import { FooterCmp } from '../ui/layout/Footer';
import { Menu } from '../ui/layout/Menu';
import { PageHeader } from '../ui/ds/PageHeader';
import { Card } from '../ui/ds/Card';
import { Btn } from '../ui/ds/Btn';
import { Badge } from '../ui/ds/Badge';
import { Input } from '../ui/ds/Input';
import { AdminSubmenu } from './AdminSubmenu';

// ── Types ─────────────────────────────────────────────────────────────────

type NotificationType = {
    type?: string;
    name?: string;
    description?: string;
    dataExample?: Record<string, any>;
};

type AdminUser = User & {
    hasApnToken?: boolean;
    activeApnTokenCount?: number;
    activeDeviceTokenCount?: number;
};

const formatJson = (value?: Record<string, any>) => JSON.stringify(value ?? {}, null, 2);

// ── Stat card ─────────────────────────────────────────────────────────────

function StatCard({
    label,
    value,
    sub,
    icon,
    color = 'blue',
}: {
    label: string;
    value: number | string;
    sub?: string;
    icon: React.ReactNode;
    color?: 'blue' | 'green' | 'red' | 'orange';
}) {
    const palette = {
        blue:   { bg: 'bg-brand-blue/10',       text: 'text-brand-blue' },
        green:  { bg: 'bg-semantic-success/10',  text: 'text-semantic-success' },
        red:    { bg: 'bg-semantic-danger/10',   text: 'text-semantic-danger' },
        orange: { bg: 'bg-brand-orange/10',      text: 'text-brand-orange' },
    };
    const p = palette[color];

    return (
        <Card className="flex items-center gap-4">
            <div className={`w-12 h-12 rounded-content flex items-center justify-center flex-shrink-0 ${p.bg}`}>
                <span className={`text-[22px] ${p.text}`}>{icon}</span>
            </div>
            <div className="min-w-0">
                <p className="tb-label">{label}</p>
                <p className={`text-[28px] font-black font-nunito leading-none ${p.text}`}>{value}</p>
                {sub && <p className="tb-caption mt-0.5">{sub}</p>}
            </div>
        </Card>
    );
}

// ── Notification Modal ────────────────────────────────────────────────────

interface NotificationModalProps {
    users: AdminUser[];
    notificationTypes: NotificationType[];
    preselectedUserId?: string;
    onClose: () => void;
    onSent: (msg: string) => void;
    onError: (msg: string) => void;
    backend: any;
}

function NotificationModal({
    users,
    notificationTypes,
    preselectedUserId,
    onClose,
    onSent,
    onError,
    backend,
}: NotificationModalProps) {
    const [targetMode,         setTargetMode]         = useState<'all' | 'user'>(preselectedUserId ? 'user' : 'all');
    const [selectedUserId,     setSelectedUserId]     = useState(preselectedUserId ?? '');
    const [selectedType,       setSelectedType]       = useState(notificationTypes[0]?.type ?? '');
    const [notificationTitle,  setNotificationTitle]  = useState('');
    const [notificationBody,   setNotificationBody]   = useState('');
    const [badge,              setBadge]              = useState('');
    const [sound,              setSound]              = useState('default');
    const [threadId,           setThreadId]           = useState('');
    const [dataJson,           setDataJson]           = useState(formatJson(notificationTypes[0]?.dataExample));
    const [sending,            setSending]            = useState(false);

    const onTypeChange = (type: string) => {
        setSelectedType(type);
        const nt = notificationTypes.find(t => t.type === type);
        setDataJson(formatJson(nt?.dataExample));
    };

    const selectedTypeDesc = notificationTypes.find(t => t.type === selectedType)?.description;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSending(true);

        try {
            let parsedData: Record<string, any> = {};
            if (dataJson.trim()) parsedData = JSON.parse(dataJson);

            if (targetMode === 'user' && !selectedUserId) {
                throw new Error('Vyberte uživatele pro odeslání notifikace.');
            }

            const payload: PushNotificationTestRequest = {
                title:     notificationTitle.trim(),
                body:      notificationBody.trim(),
                sendToAll: targetMode === 'all',
                userId:    targetMode === 'user' ? selectedUserId : undefined,
                badge:     badge ? Number(badge) : undefined,
                sound:     sound.trim() || undefined,
                category:  selectedType || undefined,
                threadId:  threadId.trim() || undefined,
                data:      selectedType ? { ...parsedData, type: selectedType } : parsedData,
            };

            const result = await backend.admin.userControllerTestPushNotification(payload);
            onSent(`Notifikace odeslána. Odesláno: ${result.sent ?? 0}, neúspěšné: ${result.failed ?? 0}, vypnuté: ${result.disabled ?? 0}.`);
            onClose();
        } catch (e: any) {
            if (e instanceof SyntaxError) {
                onError('Data musí být platný JSON.');
            } else {
                onError(e?.body?.message ?? e?.message ?? 'Notifikaci se nepodařilo odeslat.');
            }
        } finally {
            setSending(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 px-4 py-8 overflow-y-auto"
            onClick={onClose}
        >
            <div
                className="bg-bg-card rounded-hero shadow-card w-full max-w-xl overflow-hidden my-auto"
                onClick={e => e.stopPropagation()}
            >
                {/* Header */}
                <div className="bg-gradient-header px-6 py-5 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <MdNotifications className="text-[24px] text-white" />
                        <h2 className="tb-h2 text-white">Odeslat push notifikaci</h2>
                    </div>
                    <button
                        onClick={onClose}
                        className="w-9 h-9 flex items-center justify-center rounded-btn text-white/70 hover:text-white hover:bg-white/10 transition-colors"
                    >
                        <MdClose className="text-[20px]" />
                    </button>
                </div>

                {/* Body */}
                <form onSubmit={handleSubmit} className="px-6 py-6 flex flex-col gap-4">

                    {/* Target */}
                    <div>
                        <p className="tb-label mb-2">Příjemce</p>
                        <div className="grid grid-cols-2 gap-2">
                            {(['all', 'user'] as const).map(mode => (
                                <button
                                    key={mode}
                                    type="button"
                                    onClick={() => setTargetMode(mode)}
                                    className={[
                                        'h-[50px] rounded-input border-2 text-[15px] font-bold font-nunito transition-all duration-150',
                                        targetMode === mode
                                            ? 'border-brand-blue bg-brand-blue text-white'
                                            : 'border-border-neutral text-text-muted hover:border-brand-blue/40',
                                    ].join(' ')}
                                >
                                    {mode === 'all' ? '📢 Všem' : '👤 Uživateli'}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* User selector */}
                    {targetMode === 'user' && (
                        <div>
                            <p className="tb-label mb-1.5">Uživatel</p>
                            <select
                                value={selectedUserId}
                                onChange={e => setSelectedUserId(e.target.value)}
                                className="w-full h-[50px] bg-bg-input border-2 border-border-neutral rounded-input px-4 text-[15px] font-semibold text-text-primary font-nunito focus:border-brand-blue focus:outline-none transition-colors"
                                required
                            >
                                <option value="">Vyberte uživatele…</option>
                                {users.map(u => (
                                    <option key={u.id} value={u.id}>
                                        {u.firstname} {u.lastname} ({u.activeDeviceTokenCount ?? 0} zařízení)
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    {/* Notification type */}
                    {notificationTypes.length > 0 && (
                        <div>
                            <p className="tb-label mb-1.5">Typ notifikace</p>
                            <select
                                value={selectedType}
                                onChange={e => onTypeChange(e.target.value)}
                                className="w-full h-[50px] bg-bg-input border-2 border-border-neutral rounded-input px-4 text-[15px] font-semibold text-text-primary font-nunito focus:border-brand-blue focus:outline-none transition-colors"
                            >
                                {notificationTypes.map(t => (
                                    <option key={t.type ?? t.name} value={t.type ?? ''}>
                                        {t.name ?? t.type}
                                    </option>
                                ))}
                            </select>
                            {selectedTypeDesc && (
                                <p className="mt-2 px-3 py-2 bg-brand-blue/8 rounded-card tb-caption text-brand-blue">
                                    {selectedTypeDesc}
                                </p>
                            )}
                        </div>
                    )}

                    {/* Title + body */}
                    <Input
                        label="Titulek"
                        value={notificationTitle}
                        onChange={e => setNotificationTitle(e.target.value)}
                        placeholder="Název notifikace…"
                        required
                    />

                    <div className="flex flex-col gap-1.5 mb-1">
                        <p className="tb-label">Text zprávy</p>
                        <textarea
                            required
                            rows={3}
                            value={notificationBody}
                            onChange={e => setNotificationBody(e.target.value)}
                            placeholder="Obsah notifikace…"
                            className="w-full bg-bg-input border-2 border-border-neutral rounded-input px-4 py-3 text-[15px] font-semibold text-text-primary font-nunito placeholder:text-text-muted focus:border-brand-blue focus:outline-none transition-colors resize-none"
                        />
                    </div>

                    {/* Advanced: badge / sound / threadId */}
                    <div className="grid grid-cols-3 gap-3">
                        <Input label="Badge" type="number" value={badge} onChange={e => setBadge(e.target.value)} placeholder="0" />
                        <Input label="Zvuk" value={sound} onChange={e => setSound(e.target.value)} placeholder="default" />
                        <Input label="Thread ID" value={threadId} onChange={e => setThreadId(e.target.value)} placeholder="–" />
                    </div>

                    {/* Data JSON */}
                    <div className="flex flex-col gap-1.5">
                        <p className="tb-label">Data JSON</p>
                        <textarea
                            rows={5}
                            value={dataJson}
                            onChange={e => setDataJson(e.target.value)}
                            spellCheck={false}
                            className="w-full bg-bg-inputSoft border-2 border-border-neutral rounded-input px-4 py-3 font-mono text-[12px] text-text-primary focus:border-brand-blue focus:outline-none transition-colors resize-none"
                        />
                    </div>

                    {/* Actions */}
                    <div className="flex gap-3 pt-1">
                        <Btn type="button" variant="ghost" size="sm" fullWidth onClick={onClose}>
                            Zrušit
                        </Btn>
                        <Btn
                            type="submit"
                            variant="primary"
                            size="sm"
                            fullWidth
                            disabled={sending}
                            icon={<MdSend />}
                        >
                            {sending ? 'Odesílám…' : 'Odeslat'}
                        </Btn>
                    </div>
                </form>
            </div>
        </div>
    );
}

// ── Main component ─────────────────────────────────────────────────────────

export const Admin = () => {
    const userSnap = useSnapshot(userState);
    const { backend } = useApi();

    const [users,             setUsers]             = useState<AdminUser[]>([]);
    const [notificationTypes, setNotificationTypes] = useState<NotificationType[]>([]);
    const [loading,           setLoading]           = useState(false);
    const [savingUserId,      setSavingUserId]       = useState<string | null>(null);
    const [emailUserId,       setEmailUserId]       = useState<string | null>(null);
    const [deletingUserId,    setDeletingUserId]     = useState<string | null>(null);
    const [query,             setQuery]             = useState('');
    const [message,           setMessage]           = useState<string | null>(null);
    const [error,             setError]             = useState<string | null>(null);

    // Notification modal
    const [showNotifModal,    setShowNotifModal]     = useState(false);
    const [notifPreselect,    setNotifPreselect]     = useState<string | undefined>(undefined);

    const openNotifModal = (userId?: string) => {
        setNotifPreselect(userId);
        setShowNotifModal(true);
    };

    const loadAdminData = async () => {
        setLoading(true);
        setError(null);
        try {
            const [loadedUsers, loadedTypes] = await Promise.all([
                backend.admin.adminControllerAdminListUsers(),
                backend.admin.userControllerGetNotificationTypes(),
            ]);
            setUsers(loadedUsers as AdminUser[]);
            setNotificationTypes(loadedTypes);
        } catch (e: any) {
            setError(e?.body?.message ?? e?.message ?? 'Nepodařilo se načíst administraci.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (userSnap.isLoggedIn && userSnap.userProfile.role === 'Admin') {
            loadAdminData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userSnap.isLoggedIn, userSnap.userProfile.role]);

    const filteredUsers = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return users;
        return users.filter(u =>
            [u.firstname, u.lastname, u.email, u.entryFeeVS, u.role,
             u.hasApnToken ? 'apns ios push' : '',
             String(u.activeDeviceTokenCount ?? ''), String(u.activeApnTokenCount ?? '')]
                .filter(Boolean).join(' ').toLowerCase().includes(q)
        );
    }, [query, users]);

    const paidUsers              = users.filter(u => u.entryFeePaid).length;
    const unpaidUsers            = users.length - paidUsers;
    const activeDeviceTokenCount = users.reduce((n, u) => n + (u.activeDeviceTokenCount ?? 0), 0);
    const usersWithApnToken      = users.filter(u => u.hasApnToken).length;

    const payEntryFee = async (userId?: string) => {
        if (!userId) return;
        setSavingUserId(userId);
        setMessage(null); setError(null);
        try {
            await backend.admin.userControllerPayEntryFee(userId);
            setUsers(cur => cur.map(u => u.id === userId ? { ...u, entryFeePaid: true } : u));
            setMessage('Platba byla označena jako zaplacená.');
        } catch (e: any) {
            setError(e?.body?.message ?? e?.message ?? 'Platbu se nepodařilo uložit.');
        } finally {
            setSavingUserId(null);
        }
    };

    const sendPaymentInfo = async (userId?: string) => {
        if (!userId) return;
        setEmailUserId(userId);
        setMessage(null); setError(null);
        try {
            await backend.admin.userControllerSendPaymentInfo(userId);
            setMessage('Platební údaje byly odeslány na email.');
        } catch (e: any) {
            setError(e?.body?.message ?? e?.message ?? 'Email se nepodařilo odeslat.');
        } finally {
            setEmailUserId(null);
        }
    };

    const deleteUserAccount = async (user: AdminUser) => {
        if (!user.id) return;
        const name = [user.firstname, user.lastname].filter(Boolean).join(' ') || user.email;
        if (!window.confirm(`Opravdu smazat účet uživatele ${name}? Tuto akci nelze vrátit.`)) return;

        setDeletingUserId(user.id);
        setMessage(null); setError(null);
        try {
            await backend.admin.userControllerAdminDeleteUserAccount(user.id);
            setUsers(cur => cur.filter(u => u.id !== user.id));
            setMessage('Uživatelský účet byl smazán.');
        } catch (e: any) {
            setError(e?.body?.message ?? e?.message ?? 'Uživatelský účet se nepodařilo smazat.');
        } finally {
            setDeletingUserId(null);
        }
    };

    // ── Access guards ──────────────────────────────────────────────────────

    if (!userSnap.isLoggedIn) {
        return (
            <>
                <Menu />
                <PageHeader title="Administrace" subtitle="Správa uživatelů a systému" />
                <div className="max-w-xl mx-auto px-4 py-16 text-center">
                    <span className="text-[56px]">🔒</span>
                    <p className="tb-h2 text-text-primary mt-4 mb-2">Pouze pro administrátory</p>
                    <p className="tb-body text-text-muted">Pro pokračování se přihlaste administrátorským účtem.</p>
                </div>
                <FooterCmp />
            </>
        );
    }

    if (userSnap.userProfile.role !== 'Admin') {
        return (
            <>
                <Menu />
                <PageHeader title="Administrace" subtitle="Správa uživatelů a systému" />
                <div className="max-w-xl mx-auto px-4 py-16 text-center">
                    <span className="text-[56px]">🚫</span>
                    <p className="tb-h2 text-text-primary mt-4 mb-2">Nemáte oprávnění</p>
                    <p className="tb-body text-text-muted">Tato stránka je dostupná pouze administrátorům.</p>
                </div>
                <FooterCmp />
            </>
        );
    }

    // ── Main admin UI ──────────────────────────────────────────────────────

    return (
        <>
            <Menu />
            <PageHeader
                title="Administrace"
                subtitle={`${users.length} uživatelů · ${paidUsers} zaplaceno`}
                right={
                    <button
                        type="button"
                        onClick={loadAdminData}
                        disabled={loading}
                        title="Obnovit data"
                        className="w-10 h-10 flex items-center justify-center rounded-content bg-white/15 hover:bg-white/25 transition-colors text-white disabled:opacity-50"
                    >
                        <MdRefresh className={`text-[22px] ${loading ? 'animate-spin' : ''}`} />
                    </button>
                }
            />
            <AdminSubmenu />

            <main className="max-w-7xl mx-auto px-4 md:px-8 py-6 flex flex-col gap-6">

                {/* Feedback banner */}
                {(message || error) && (
                    <div className={[
                        'flex items-center gap-3 rounded-content px-4 py-3',
                        error
                            ? 'bg-semantic-danger/10 border border-semantic-danger/20'
                            : 'bg-semantic-success/10 border border-semantic-success/20',
                    ].join(' ')}>
                        <Badge variant={error ? 'danger' : 'success'}>
                            {error ? 'Chyba' : 'OK'}
                        </Badge>
                        <span className="tb-body text-text-primary">{error ?? message}</span>
                        <button
                            onClick={() => { setError(null); setMessage(null); }}
                            className="ml-auto text-text-muted hover:text-text-primary transition-colors"
                        >
                            <MdClose className="text-[18px]" />
                        </button>
                    </div>
                )}

                {/* Stat cards */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
                    <StatCard label="Celkem" value={users.length}        icon={<MdPeople />}     color="blue"  />
                    <StatCard label="Zaplaceno" value={paidUsers}        icon={<MdCheckCircle />} color="green" />
                    <StatCard label="Nezaplaceno" value={unpaidUsers}    icon={<MdWarning />}    color="red"   />
                    <StatCard
                        label="Push zařízení"
                        value={activeDeviceTokenCount}
                        sub={`${usersWithApnToken} uživatelů s APNs`}
                        icon={<MdPhoneIphone />}
                        color="orange"
                    />
                </div>

                {/* Controls: search + send notification */}
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                    <div className="relative w-full sm:w-80">
                        <MdSearch className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-[20px] text-text-muted" />
                        <input
                            value={query}
                            onChange={e => setQuery(e.target.value)}
                            placeholder="Hledat uživatele…"
                            className="w-full h-[50px] bg-bg-input border-2 border-border-neutral rounded-input pl-11 pr-4 text-[15px] font-semibold text-text-primary font-nunito placeholder:text-text-muted focus:border-brand-blue focus:outline-none transition-colors"
                        />
                    </div>
                    <Btn
                        variant="primary"
                        size="sm"
                        icon={<MdNotifications className="text-[18px]" />}
                        onClick={() => openNotifModal()}
                    >
                        Odeslat notifikaci
                    </Btn>
                </div>

                {/* Users table */}
                <Card padding="none">
                    {/* Table header */}
                    <div className="hidden md:grid md:grid-cols-[2fr_2fr_1fr_1fr_1.5fr_1.5fr_2fr] gap-0 px-5 py-3 bg-bg-app border-b border-border-neutral rounded-t-content">
                        {['Uživatel', 'Email', 'VS', 'Body', 'Platba', 'Push', 'Akce'].map(col => (
                            <span key={col} className="tb-label">{col}</span>
                        ))}
                    </div>

                    {loading && (
                        <div className="flex items-center justify-center py-16">
                            <MdRefresh className="text-[32px] text-brand-blue animate-spin" />
                        </div>
                    )}

                    {!loading && filteredUsers.length === 0 && (
                        <div className="flex flex-col items-center justify-center py-16 gap-3">
                            <span className="text-[48px]">🔍</span>
                            <p className="tb-h3 text-text-primary">Žádní uživatelé</p>
                            <p className="tb-caption">Zkuste jiný vyhledávací dotaz.</p>
                        </div>
                    )}

                    {!loading && filteredUsers.map((user, idx) => (
                        <div
                            key={user.id}
                            className={[
                                'flex flex-col md:grid md:grid-cols-[2fr_2fr_1fr_1fr_1.5fr_1.5fr_2fr] items-start md:items-center gap-3 md:gap-0 px-5 py-4 border-b border-divider-row last:border-b-0 hover:bg-bg-app transition-colors',
                                idx % 2 === 0 ? '' : '',
                            ].join(' ')}
                        >
                            {/* Name + role */}
                            <div>
                                <p className="tb-h4 text-text-primary leading-tight">
                                    {user.firstname} {user.lastname}
                                </p>
                                <p className="tb-caption capitalize">{user.role}</p>
                            </div>

                            {/* Email */}
                            <p className="tb-body text-text-muted truncate max-w-[180px]">{user.email}</p>

                            {/* VS */}
                            <p className="tb-body text-text-muted">{user.entryFeeVS ?? '—'}</p>

                            {/* Points */}
                            <p className="text-[16px] font-black font-nunito text-text-primary">{user.points ?? 0}</p>

                            {/* Payment */}
                            <div>
                                {user.entryFeePaid ? (
                                    <Badge variant="success">
                                        <AiFillCheckCircle className="inline mr-1" />Zaplaceno
                                    </Badge>
                                ) : (
                                    <Badge variant="danger">Nezaplaceno</Badge>
                                )}
                            </div>

                            {/* Push devices */}
                            <div className="flex flex-col gap-0.5">
                                <span className="text-[14px] font-bold font-nunito text-text-primary">
                                    {user.activeDeviceTokenCount ?? 0} aktivní
                                </span>
                                <span className="tb-caption">{user.activeApnTokenCount ?? 0} APNs</span>
                                {user.hasApnToken ? (
                                    <Badge variant="success" className="mt-0.5 w-fit">iOS push</Badge>
                                ) : (
                                    <span className="tb-micro text-text-muted">Bez APNs</span>
                                )}
                            </div>

                            {/* Actions */}
                            <div className="flex flex-wrap gap-2">
                                {!user.entryFeePaid && (
                                    <button
                                        type="button"
                                        onClick={() => payEntryFee(user.id)}
                                        disabled={savingUserId === user.id}
                                        title="Označit jako zaplacené"
                                        className="flex items-center gap-1 h-8 px-3 bg-semantic-success text-white rounded-btn text-[12px] font-bold font-nunito hover:opacity-90 transition-opacity disabled:opacity-50"
                                    >
                                        <MdPaid className="text-[15px]" />
                                        {savingUserId === user.id ? '…' : 'Zaplatit'}
                                    </button>
                                )}
                                <button
                                    type="button"
                                    onClick={() => sendPaymentInfo(user.id)}
                                    disabled={emailUserId === user.id}
                                    title="Odeslat platební údaje emailem"
                                    className="flex items-center gap-1 h-8 px-3 bg-text-primary text-white rounded-btn text-[12px] font-bold font-nunito hover:opacity-90 transition-opacity disabled:opacity-50"
                                >
                                    <MdEmail className="text-[15px]" />
                                    {emailUserId === user.id ? '…' : 'Email (PaymentInfo)'}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => openNotifModal(user.id)}
                                    title="Odeslat push notifikaci"
                                    className="flex items-center gap-1 h-8 px-3 bg-brand-blue text-white rounded-btn text-[12px] font-bold font-nunito hover:opacity-90 transition-opacity"
                                >
                                    <MdNotifications className="text-[15px]" />
                                    Push
                                </button>
                                <button
                                    type="button"
                                    onClick={() => deleteUserAccount(user)}
                                    disabled={deletingUserId === user.id}
                                    title="Smazat účet"
                                    className="flex items-center gap-1 h-8 px-3 bg-semantic-danger text-white rounded-btn text-[12px] font-bold font-nunito hover:opacity-90 transition-opacity disabled:opacity-50"
                                >
                                    <MdDelete className="text-[15px]" />
                                    {deletingUserId === user.id ? '…' : 'Smazat'}
                                </button>
                            </div>
                        </div>
                    ))}
                </Card>

                {/* User count */}
                {!loading && filteredUsers.length > 0 && (
                    <p className="tb-caption text-center">
                        Zobrazeno {filteredUsers.length} z {users.length} uživatelů
                    </p>
                )}
            </main>

            <FooterCmp />

            {/* Notification modal */}
            {showNotifModal && (
                <NotificationModal
                    users={users}
                    notificationTypes={notificationTypes}
                    preselectedUserId={notifPreselect}
                    backend={backend}
                    onClose={() => setShowNotifModal(false)}
                    onSent={msg => { setMessage(msg); setError(null); }}
                    onError={msg => { setError(msg); setMessage(null); }}
                />
            )}
        </>
    );
};
